import * as React from "react";
import { icons as lucideIcons } from "lucide-react";
import {
    Activity,
    AlignCenter, AlignCenterVertical,
    AlignEndHorizontal,
    AlignEndVertical,
    AlignHorizontalDistributeCenter,
    AlignStartHorizontal,
    AlignStartVertical,
    AlignVerticalDistributeCenter,
    ArrowLeft,
    Bold,
    Building,
    Camera,
    Car,
    Check,
    CheckSquare,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    CloudSun,
    Code,
    Compass,
    Copy,
    Database,
    Download,
    Edit,
    PenLine,
    ExternalLink,
    Eye,
    FileCode,
    FileText,
    Filter,
    Flag,
    Folder,
    FolderOpen,
    GitBranch,
    GitCompare,
    Globe,
    GraduationCap,
    Heading1,
    Heading2,
    Heading3,
    HelpCircle,
    History,
    Home,
    IndentIncrease,
    Info,
    Italic,
    Layers,
    Library,
    Lightbulb,
    Link as LinkIcon,
    List,
    ListChecks,
    ListOrdered,
    Loader,
    LogOut,
    Mail,
    Maximize2,
    Megaphone,
    Minimize2,
    MapPin,
    MessageCircle,
    MessageSquare,
    MessagesSquare,
    MountainSnow,
    Navigation,
    Pause,
    Phone,
    ChartPie,
    Plug,
    PlugZap,
    Plus,
    Play,
    QrCode,
    Redo2,
    RefreshCw,
    Repeat,
    RotateCw,
    Rocket,
    Search,
    Settings,
    Shield,
    ShoppingBag,
    ShoppingCart,
    SidebarOpen,
    Smile,
    Square, Table as TableIcon,
    ThumbsDown,
    ThumbsUp,
    Trash2,
    Triangle,
    Undo2,
    Wallet,
    Wrench,
    User,
    UserCheck,
    UserCog,
    UserPlus,
    UserMinus, UsersRound,
    X,
    Zap
} from "lucide-react";

/** Fluent UI icon name → lucide-react component map.
 *  Anything not in here resolves to `null` (the consumer renders nothing). */
export const FLUENT_ICON_MAP: Record<string, React.ComponentType<any>> = {
  Add: Plus,
  Admin: Shield,
  AddFriend: UserPlus,
  AddTo: Plus,
  AddToShoppingList: ShoppingBag,
  AlignHorizontalCenter: AlignCenter,
  AlignHorizontalLeft: AlignStartHorizontal,
  AlignHorizontalRight: AlignEndHorizontal,
  AlignVerticalBottom: AlignEndVertical,
  AlignVerticalCenter: AlignCenterVertical,
  AlignVerticalTop: AlignStartVertical,
  AppIconDefaultAdd: Plus,
  Back: ArrowLeft,
  Bold: Bold,
  BranchFork2: GitBranch,
  BuildQueue: ListChecks,
  BulletedList: List,
  BulletedList2: List,
  Camera: Camera,
  CannedChat: MessagesSquare,
  Cancel: X,
  Car: Car,
  Chat: MessageCircle,
  CheckMark: Check,
  CheckList: ListChecks,
  CheckboxComposite: CheckSquare,
  ChevronDown: ChevronDown,
  ChevronLeft: ChevronLeft,
  ChevronRight: ChevronRight,
  ChevronUp: ChevronUp,
  ChromeRestore: Minimize2,
  CityNext: Building,
  CityNext2: Building,
  Clear: X,
  CloudWeather: CloudSun,
  Code: Code,
  Compass: Compass,
  CommentAdd: MessageSquare,
  Compare: GitCompare,
  Contact: User,
  Copy: Copy,
  Database: Database,
  Delete: Trash2,
  Design: Layers,
  Dislike: ThumbsDown,
  Document: FileText,
  Download: Download,
  EMI: Activity,
  Edit: Edit,
  EditNote: PenLine,
  Emoji2: Smile,
  EndPointSolid: Square,
  ExternalBuild: ExternalLink,
  FileCode: FileCode,
  Filter: Filter,
  Flag: Flag,
  FolderHorizontal: Folder,
  FollowUser: UserPlus,
  FullScreen: Maximize2,
  GlobeNet: Globe,
  GroupList: UsersRound,
  Header1: Heading1,
  Header2: Heading2,
  Header3: Heading3,
  History: History,
  Home: Home,
  HorizontalDistributeCenter: AlignHorizontalDistributeCenter,
  IncreaseIndentArrow: IndentIncrease,
  Info: Info,
  Italic: Italic,
  Leave: LogOut,
  Learning: GraduationCap,
  Library: Library,
  Lightbulb: Lightbulb,
  LightingBolt: Zap,
  Like: ThumbsUp,
  Link: LinkIcon,
  Location: MapPin,
  Mail: Mail,
  MapDirections: Navigation,
  MapLayers: Layers,
  Megaphone: Megaphone,
  Message: MessageCircle,
  MiniExpand: Maximize2,
  Money: Wallet,
  MountainClimbing: MountainSnow,
  NumberedList: ListOrdered,
  OfficeChat: MessageSquare,
  OpenFolderHorizontal: FolderOpen,
  OpenInNewWindow: ExternalLink,
  Orientation2: RotateCw,
  PDF: FileText,
  Pause: Pause,
  Phone: Phone,
  Photo2Add: Camera,
  PieSingle: ChartPie,
  Play: Play,
  PlugConnected: PlugZap,
  PlugDisconnected: Plug,
  QRCode: QrCode,
  Redo: Redo2,
  Refresh: RefreshCw,
  Repair: Wrench,
  RepeatOne: Repeat,
  Rocket: Rocket,
  Room: MapPin,
  SchoolDataSyncLogo: Loader,
  Search: Search,
  Settings: Settings,
  Shield: Shield,
  ShoppingCart: ShoppingCart,
  SidePanel: SidebarOpen,
  Stack: Layers,
  StatusCircleQuestionMark: HelpCircle,
  Table: TableIcon,
  TextDocument: FileText,
  Undo: Undo2,
  UserEvent: UserCheck,
  UserGauge: UserCog,
  UserOptional: User,
  UserPause: UserCog,
  UserRemove: UserMinus,
  UserSync: UserCheck,
  VerticalDistributeCenter: AlignVerticalDistributeCenter,
  View: Eye,
  Warning: Triangle,
  ZoomToFit: Maximize2,
};

// Track names we've already complained about so a missing icon used in a
// loop doesn't spam the console.
const _warnedMissing = new Set<string>();

/**
 * Resolve a name to a renderable icon component.
 *
 * Look-up order:
 *   1. `FLUENT_ICON_MAP` — Fluent UI icon names mapped to a chosen lucide
 *      equivalent (handles legacy data and overrides our visual choices).
 *   2. `lucide-react`'s `icons` registry — any PascalCase lucide name.
 *
 * The icon picker stores lucide names directly, so this two-step lookup lets
 * a user pick any of the ~1500 lucide icons without us having to hard-code
 * each one in `FLUENT_ICON_MAP`.
 */
export function fluentIconComponent(
  name: string | undefined | null,
): React.ComponentType<any> | null {
  if (!name) return null;
  const hit = FLUENT_ICON_MAP[name];
  if (hit) return hit;
  const direct = (lucideIcons as Record<string, React.ComponentType<any>>)[name];
  if (direct) return direct;
  if (process.env.NODE_ENV !== "production" && !_warnedMissing.has(name)) {
    _warnedMissing.add(name);
    // eslint-disable-next-line no-console
    console.warn(`[fluent-icon-map] no mapping for iconName "${name}" — neither in FLUENT_ICON_MAP nor in lucide-react`);
  }
  return null;
}
